import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["data-vr-focus", "aria-label", "aria-labelledby", "autoFocus", "checked", "children", "color", "defaultChecked", "description", "descriptionLayout", "disabled", "id", "name", "onChange", "readOnly", "size", "value", "alignment", "inline", "labelStyles"];
import { forwardRef, useContext, useMemo } from 'react';
import devLogger from 'react-utils/devLogger';
import useUniqueId from 'react-utils/hooks/useUniqueId';
import styled, { css } from 'foundations-theming/utils';
import { FieldsetContext } from '../../context/FieldsetContext';
import { ToggleInputWrapper } from '../shared-components/ToggleInputWrapper';
import { uiFocus } from '../../utils/color';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getRadioDeprecatedPatternStyles = (theme, hasChildren, size) => {
  if (!hasChildren && size === 'sm') {
    return css(["font-size:0.915em;margin-right:", ";vertical-align:baseline;"], theme.space[200]);
  } else if (!hasChildren) {
    return css(["font-size:1.143em;margin-right:", ";vertical-align:text-bottom;"], theme.space[300]);
  }
  return '';
};
export const Radio = styled(ToggleInputWrapper).withConfig({
  displayName: "RadioInput__Radio",
  componentId: "sc-14d4deo-0"
})(["", " ", ";", " ", ""], ({
  $hasChildren
}) => $hasChildren && `user-select: none;`, ({
  $hasChildren
}) => !$hasChildren && css(["min-height:0;&::after{display:none;}display:inline-block;"]), ({
  $hasChildren,
  $size,
  theme
}) => getRadioDeprecatedPatternStyles(theme, $hasChildren, $size), props => Object.assign({}, props.$cssStyles));
const PrivateRadioDot = styled.circle.withConfig({
  displayName: "RadioInput__PrivateRadioDot",
  componentId: "sc-14d4deo-1"
})([""]);
const PrivateRadioIndicator = styled.span.withConfig({
  displayName: "RadioInput__PrivateRadioIndicator",
  componentId: "sc-14d4deo-2"
})(props => {
  const {
    theme
  } = props;
  const $color = props.$color;
  return {
    flexShrink: 0,
    transition: 'all 150ms ease-out',
    backgroundColor: theme.color['fill-surface-default'],
    color: $color ? theme.color[$color] : theme.color['fill-tertiary-pressed'],
    height: '1.25em',
    left: '0.0625em',
    position: 'relative',
    width: '1.25em',
    border: 0,
    borderRadius: '100%'
  };
});
const PrivateRadioCircle = styled.circle.withConfig({
  displayName: "RadioInput__PrivateRadioCircle",
  componentId: "sc-14d4deo-3"
})(props => {
  const {
    $readOnly,
    $disabled
  } = props;
  const theme = props.theme;
  return {
    fill: $readOnly || $disabled ? theme.color['fill-field-disabled'] : theme.color['fill-field-default-alt'],
    stroke: $readOnly || $disabled ? theme.color['border-core-disabled'] : theme.color['border-core-default']
  };
});
const PrivateRadioInput = styled.input.withConfig({
  displayName: "RadioInput__PrivateRadioInput",
  componentId: "sc-14d4deo-4"
})(props => {
  const theme = props.theme;
  const $color = props.$color;
  return {
    // sr-only class styles
    position: 'absolute !important',
    top: '0 !important',
    width: `${theme.space[25]} !important`,
    height: `${theme.space[25]} !important`,
    margin: `-${theme.space[25]} !important`,
    padding: '0 !important',
    overflow: 'hidden !important',
    clip: 'rect(0, 0, 0, 0) !important',
    border: '0 !important',
    '&:checked': {
      [`+ ${PrivateRadioIndicator}`]: {
        color: $color ? theme.color[$color] : theme.color['icon-interactive-default'],
        [PrivateRadioDot]: {
          fill: 'currentColor'
        }
      }
    },
    '&:checked:not([disabled]):not([readonly])': {
      [`+ ${PrivateRadioIndicator}`]: {
        [PrivateRadioCircle]: {
          stroke: 'currentColor'
        }
      }
    },
    '&:focus-visible': {
      [`&:not([readonly]) + ${PrivateRadioIndicator}`]: Object.assign({}, uiFocus, {
        boxShadow: 'rgba(0, 208, 228, 0.3) 0px 0px 4px 1px, rgb(0, 208, 228) 0px 0px 0px 1px !important',
        backgroundColor: theme.color['fill-tertiary-default'],
        borderColor: 'currentColor'
      }),
      [`&[readonly] + ${PrivateRadioIndicator}`]: {
        outline: 'auto'
      }
    }
  };
});
export const RadioInput = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      'data-vr-focus': dataVrFocus,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      autoFocus,
      checked,
      children,
      color,
      defaultChecked,
      description,
      descriptionLayout = 'vertical',
      disabled,
      id,
      name,
      onChange,
      readOnly,
      size,
      value,
      alignment = 'start',
      inline,
      labelStyles
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const {
    size: fieldSize,
    disabled: fieldDisabled
  } = useContext(FieldsetContext);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const descriptionId = useUniqueId('radio-');
  const childWrapperId = useUniqueId('radio-content-');
  const computedSize = size || fieldSize;
  const computedDisabled = disabled || fieldDisabled;
  const ariaLabelledByIds = `${ariaLabelledBy !== null && ariaLabelledBy !== void 0 ? ariaLabelledBy : ''} ${children ? childWrapperId : ''}`.trim();
  if (process.env.NODE_ENV !== 'production') {
    if (!name) {
      devLogger.warn({
        message: 'RadioInput: `name` must be specified. Try wrapping with `ToggleGroup`.',
        key: 'RadioInput: name',
        url: 'https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/radio#defining_a_radio_group'
      });
    }
    if (children == null && !ariaLabel && !ariaLabelledBy) {
      devLogger.warn({
        message: "RadioInput: `aria-label` or `aria-labelledby` is required if you don't define `children`.",
        key: 'Checkbox: no label'
      });
    }
    if (ariaLabel && (children || ariaLabelledBy)) {
      devLogger.warn({
        message: 'RadioInput: `aria-label` should not be used with `children` or `aria-labelledby`.',
        key: 'RadioInput: aria-label hides children',
        url: 'https://git.hubteam.com/HubSpot/Components/pull/8473/files#r1011220'
      });
    }
  }
  return /*#__PURE__*/_jsx(Radio, Object.assign({
    $cssStyles: cssStyles,
    $hasChildren: children != null,
    $size: computedSize,
    checked: checked,
    childWrapperId: childWrapperId,
    description: description,
    descriptionId: descriptionId,
    descriptionLayout: descriptionLayout,
    disabled: computedDisabled,
    alignment: alignment,
    inline: inline,
    labelStyles: labelStyles
  }, domProps, {
    input: /*#__PURE__*/_jsx(PrivateRadioInput, {
      "data-vr-focus": dataVrFocus,
      "aria-describedby": description ? descriptionId : undefined,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledByIds,
      autoFocus: autoFocus,
      checked: checked,
      defaultChecked: defaultChecked,
      disabled: computedDisabled,
      id: id,
      name: name,
      onChange: evt => {
        if (disabled || readOnly) {
          evt.preventDefault();
          return;
        }
        if (onChange) onChange(evt);
      },
      readOnly: readOnly,
      type: "radio",
      value: value,
      $color: color
    }),
    indicator: useMemo(() => {
      return /*#__PURE__*/_jsx(PrivateRadioIndicator, {
        "data-indicator": true,
        $color: color,
        "aria-hidden": true,
        children: /*#__PURE__*/_jsx("svg", {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 22 22",
          children: /*#__PURE__*/_jsxs("g", {
            fill: "none",
            fillRule: "evenodd",
            transform: "translate(1 1)",
            children: [/*#__PURE__*/_jsx(PrivateRadioCircle, {
              cx: "10",
              cy: "10",
              r: "10",
              stroke: "currentColor",
              strokeWidth: "1",
              $readOnly: readOnly,
              $disabled: disabled
            }), /*#__PURE__*/_jsx(PrivateRadioDot, {
              cx: "10",
              cy: "10",
              r: "5"
            })]
          })
        })
      });
    }, [color, readOnly, disabled]),
    onChange: onChange,
    readOnly: readOnly,
    ref: ref,
    size: computedSize,
    children: children
  }));
});